import React, { useEffect, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import { ClipboardCheck, Check, X, User, MessageSquare } from "lucide-react";

type Req = {
  id: string;
  type: "LEAVE" | "SHORT_LEAVE" | "OVERTIME_BEFORE" | "OVERTIME_AFTER";
  leaveKind?: "ANNUAL" | "CASUAL" | null;
  startDate: string;
  endDate: string | null;
  days: number | null;
  hours: number | null;
  reason: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  createdAt: string;
  employee: {
    id: string;
    name: string;
    email: string;
    department: string | null;
    leaveBalanceDays: number;
    annualLeaveDays?: number;
    casualLeaveDays?: number;
  };
};

const TYPE_LABEL: Record<string, string> = {
  LEAVE: "🏖️ Leave",
  SHORT_LEAVE: "🕐 Short Leave (2h)",
  OVERTIME_BEFORE: "⏰ Overtime (pre-approval)",
  OVERTIME_AFTER: "⌛ Overtime (already worked)",
};

function typeLabel(r: { type: string; leaveKind?: string | null }): string {
  if (r.type === "LEAVE") return r.leaveKind === "CASUAL" ? "🏝️ Casual Leave" : "🏖️ Annual Leave";
  return TYPE_LABEL[r.type];
}

export default function RequestApprovals() {
  const [rows, setRows] = useState<Req[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"pending" | "all">("pending");
  const [noteFor, setNoteFor] = useState<{ req: Req; decision: "APPROVED" | "REJECTED" } | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get<Req[]>(`/requests/${tab}`)
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab]);

  const openDecision = (req: Req, decision: "APPROVED" | "REJECTED") => {
    setNoteFor({ req, decision });
    setNote("");
  };

  const submit = async () => {
    if (!noteFor) return;
    setSaving(true);
    try {
      const path = noteFor.decision === "APPROVED" ? "approve" : "reject";
      await api.post(`/requests/${noteFor.req.id}/${path}`, { note: note.trim() || undefined });
      toast.success(`Request ${noteFor.decision.toLowerCase()}`);
      setNoteFor(null);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const daysBetween = (a: string, b: string | null): string => {
    if (!b) return "";
    const d1 = new Date(a); const d2 = new Date(b);
    return `${new Date(a).toLocaleDateString()} → ${new Date(b).toLocaleDateString()}`;
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <ClipboardCheck className="text-indigo-600" size={26}/>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Request Approvals</h1>
          <p className="text-sm text-gray-500">Review leave & overtime requests from your team</p>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {(["pending", "all"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            {t === "pending" ? "Pending" : "All Requests"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
          {tab === "pending" ? "No pending requests. 🎉" : "No requests found."}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <User size={14} className="text-gray-400"/>
                    <div className="font-semibold text-gray-900">{r.employee.name}</div>
                    {r.employee.department && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{r.employee.department}</span>
                    )}
                    {r.type === "LEAVE" && (
                      <span className="text-xs text-gray-500">
                        {r.leaveKind === "CASUAL"
                          ? <>Casual: <strong>{(r.employee.casualLeaveDays ?? 0).toFixed(1)}</strong>d</>
                          : <>Annual: <strong>{(r.employee.annualLeaveDays ?? 0).toFixed(1)}</strong>d</>}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 mb-1">
                    <strong>{typeLabel(r)}</strong> —{" "}
                    {r.type === "LEAVE" ? (
                      <>
                        {daysBetween(r.startDate, r.endDate)} · <strong>{r.days} day{r.days === 1 ? "" : "s"}</strong>
                      </>
                    ) : r.type === "SHORT_LEAVE" ? (
                      <>
                        {new Date(r.startDate).toLocaleDateString()} · <strong>2 hours</strong>
                      </>
                    ) : (
                      <>
                        {new Date(r.startDate).toLocaleDateString()} · <strong>{r.hours} hr{r.hours === 1 ? "" : "s"}</strong>
                      </>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 flex items-start gap-1.5">
                    <MessageSquare size={13} className="text-gray-400 mt-0.5 shrink-0"/>
                    <span>{r.reason || <span className="italic text-gray-400">No reason provided</span>}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">Submitted {new Date(r.createdAt).toLocaleString()}</div>
                </div>
                {r.status === "PENDING" ? (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => openDecision(r, "APPROVED")}
                      className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
                      <Check size={14}/> Approve
                    </button>
                    <button onClick={() => openDecision(r, "REJECTED")}
                      className="flex items-center gap-1 bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-red-700">
                      <X size={14}/> Reject
                    </button>
                  </div>
                ) : (
                  <span className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium ${
                    r.status === "APPROVED" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                  }`}>
                    {r.status}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {noteFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setNoteFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className={`px-5 py-4 border-b ${noteFor.decision === "APPROVED" ? "bg-green-50" : "bg-red-50"} flex items-center justify-between`}>
              <div className={`text-lg font-semibold ${noteFor.decision === "APPROVED" ? "text-green-800" : "text-red-800"}`}>
                {noteFor.decision === "APPROVED" ? "✅ Approve" : "❌ Reject"} Request
              </div>
              <button onClick={() => setNoteFor(null)} className="text-gray-500 hover:text-gray-800"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm text-gray-600">
                <strong>{noteFor.req.employee.name}</strong> — {typeLabel(noteFor.req)}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                  placeholder="Optional note for the employee..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => setNoteFor(null)} className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">Cancel</button>
                <button onClick={submit} disabled={saving}
                  className={`px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50 ${
                    noteFor.decision === "APPROVED" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                  }`}>
                  {saving ? "Saving..." : noteFor.decision === "APPROVED" ? "Confirm Approve" : "Confirm Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
